import React, { useState, useEffect, useRef, useCallback } from 'react';
import IconButton from './IconButton';
import InputDate from './InputDate';
import InputTime from './InputTime';
import SaveStatusFeedback, { SaveStatus } from './SaveStatusFeedback';
import Icon from './icons/Icon';
import Checkbox from './Checkbox';
import MenuButton from './MenuButton';
import InputNumber from './InputNumber';
import { Todo, StockItem } from '../types/data';
import { useHabits } from '../hooks/useHabits';
import { useTags } from '../hooks/useTags';
import { useStocks } from '../hooks/useStocks';
import InputTodoTitle from './InputTodoTitle';
import InputTodoTags from './InputTodoTags';
import './DrawerTodoDetail.css';
import { TodoSyncConflictError, syncTodoChange, SyncRow } from '../utils/repos/todoSync';
import { toCamelCase } from '../utils/dataTransform';
import type { TodoEntity } from '../types/data';
import { isShoppingHabitId } from '../utils/habits';
import { createTokyoDateTime, formatTokyoDate, getTokyoDateParts } from '../utils/dateUtils';

interface DrawerTodoDetailProps {
  /** 表示するTodo項目 */
  todo?: Todo;
  /** ドロワーの表示状態 */
  isOpen: boolean;
  /** ドロワーを閉じる時のコールバック */
  onClose: () => void;
  /** Todo保存時のコールバック */
  onSave?: (todo: Todo, original?: Todo) => Promise<Todo | void> | Todo | void;
  /** 新規Todo作成時のコールバック */
  onCreate?: (todo: Omit<Todo, 'id'> & { id?: string }) => Promise<Todo | void> | Todo | void;
  onCreated?: (todo: Todo) => void;
  /** Todo削除時のコールバック */
  onDelete?: (todoId: string) => void;
  /** Todo複製時のコールバック */
  onDuplicate?: (todo: Todo) => void;
  /** 前の予定に移動時のコールバック */
  onPreviousTodo?: () => void;
  /** 次の予定に移動時のコールバック */
  onNextTodo?: () => void;
  /** 前の予定が存在するかどうか */
  hasPreviousTodo?: boolean;
  /** 次の予定が存在するかどうか */
  hasNextTodo?: boolean;
  /** Todoの日付が変更された時のコールバック */
  onDateChange?: (newDate: string) => void;
  /** TodoPageのビュー種別（'date' はカレンダー相当） */
  viewMode?: 'date' | 'tag' | 'habit';
}

const DrawerTodoDetail: React.FC<DrawerTodoDetailProps> = ({
  todo,
  isOpen,
  onClose,
  onSave,
  onCreate,
  onCreated,
  onDelete,
  onDuplicate,
  onPreviousTodo,
  onNextTodo,
  hasPreviousTodo = false,
  hasNextTodo = false,
  onDateChange,
  viewMode
}) => {
  // ローカル状態管理（UI即座更新用）
  const [localTodo, setLocalTodo] = useState<Todo | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isCreated, setIsCreated] = useState(false); // 新規作成済みフラグ
  
  // 習慣データ管理: 値（配列）activeHabits を直接使う
  // 目的: 依存配列に安全に入れられる値を利用して無限ループを回避する
  const { activeHabits, createHabit } = useHabits();
  
  // タグデータ管理
  const { activeTags, createNewTag, getHabitTagIds } = useTags();
  
  // ストックデータ管理
  const { activeStocks, createStock, activeStockCategories } = useStocks();
  
  
  // タイトル入力の習慣連携状態
  const [isHabitSelected, setIsHabitSelected] = useState(false);
  
  // タグ入力の表示状態
  const [showTagInput, setShowTagInput] = useState(false);
  const lastAppliedHabitDefaultTagIdsRef = useRef<string[]>([]);
  const lastAppliedHabitDefaultSourceHabitIdRef = useRef<string | undefined>(undefined);
  
  // 買い物リスト検索の状態
  const [shoppingSearchTerm, setShoppingSearchTerm] = useState('');
  const [showShoppingDropdown, setShowShoppingDropdown] = useState(false);
  const shoppingWrapperRef = useRef<HTMLDivElement>(null);
  
  // 新規作成かどうかの判定
  const isNewTodo = todo?.id === '' && !isCreated;
  
  // 新規Todoが入力されているかどうかの判定
  const hasNewTodoInput = useCallback((todo: Todo) => {
    const hasTitle = (todo.title || '').trim() !== '';
    const hasBody = ((todo.body || '').trim()) !== '';
    const hasHabit = !!todo.habitId;
    const hasStocks = (todo.stockItems?.length ?? 0) > 0;
    const hasTags = (todo.todoTagIds?.length ?? 0) > 0;
    return hasTitle || hasBody || hasHabit || hasStocks || hasTags;
  }, []);
  
  // DOM参照
  const drawerRef = useRef<HTMLDivElement>(null);
  
  // デバウンスタイマーの参照
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 成功メッセージ自動削除タイマーの参照
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 成功メッセージの自動削除
  // 何をしているか: 保存成功表示から2秒後にステータスをidleへ戻す
  const clearSuccessMessage = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    successTimeoutRef.current = setTimeout(() => {
      setSaveStatus('idle');
    }, 2000);
  }, []);
  
  const isCreatingRef = useRef(false); // 新規作成の同時実行を防ぐためのフラグ（React 18 StrictModeやデバウンスの競合対策）
  // 作成が完了したTodoを保持し、親のprops反映前に続いた入力を更新として扱う
  const [conflict, setConflict] = useState<TodoSyncConflictError | null>(null);
  const conflictRef = useRef<TodoSyncConflictError | null>(null);
  const savedTodoRef = useRef<Todo | null>(null);
  const creationIDRef = useRef<string | null>(null);
  const saveQueueRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const editingSessionRef = useRef(0);
  const createdTodoRef = useRef<Todo | null>(null);
  const latestLocalTodoRef = useRef<Todo | null>(null);
  const isComposingRef = useRef(false); // IME変換中を同期的に判定するためのref
  const prevTodoIdRef = useRef<string | null>(null); // 前回同期した Todo の ID（ID が変わった時だけ props → local に同期）

  const applySavedTodo = useCallback((saved: Todo, sent: Todo) => {
    const current = latestLocalTodoRef.current;
    if (!current || (current.id && current.id !== saved.id)) return;
    const next = { ...saved };
    const fields = ['title', 'body', 'done', 'canceled', 'deleted', 'startAt', 'doneAt', 'habitId', 'todoTagIds', 'stockItems'] as const;
    fields.forEach(field => {
      if (JSON.stringify(current[field]) !== JSON.stringify(sent[field])) Object.assign(next, { [field]: current[field] });
    });
    savedTodoRef.current = saved;
    latestLocalTodoRef.current = next;
    setLocalTodo(next);
  }, []);

  // 実際の保存処理（成功・失敗のハンドリング付き）
  const performSaveNow = useCallback(async (todo: Todo): Promise<boolean> => {
    if (conflictRef.current) return false;
    const session = editingSessionRef.current;
    try {
      setSaveStatus('saving');
      setSaveError(undefined);

      // 変換中の本文を、先に予約されていた保存でも送信しない。
      if (isComposingRef.current) {
        setSaveStatus('dirty');
        return false;
      }

      if (isNewTodo) {
        // 何をしているか: タイトル/本文のいずれかが入力されていなければ新規作成しない
        if (!hasNewTodoInput(todo)) {
          setSaveStatus('idle');
          return true;
        }

        // 親からID付きTodoが反映される前の入力は、同じTodoの更新として保存する
        if (createdTodoRef.current) {
          const updatedTodo = {
            ...todo,
            id: createdTodoRef.current.id,
            createdAt: createdTodoRef.current.createdAt,
            modifiedAt: createdTodoRef.current.modifiedAt,
          };
          const saved = await Promise.resolve(onSave?.(updatedTodo, savedTodoRef.current ?? createdTodoRef.current ?? undefined));
          applySavedTodo(saved || updatedTodo, updatedTodo);
        // 何をしているか: 新規作成の同時実行（二重発火）を防止
        } else if (isCreatingRef.current) {
          // 作成完了時に最新の入力内容をまとめて保存する
          return false;
        } else {
          isCreatingRef.current = true;

          try {
            // デバウンスタイマーが持つ古い値ではなく、現時点の入力値で作成する
            const todoToCreate = latestLocalTodoRef.current ?? todo;
            const { id, ...todoWithoutId } = todoToCreate;
            creationIDRef.current ??= crypto.randomUUID();
            const createdTodo = await Promise.resolve(onCreate?.({ ...todoWithoutId, id: creationIDRef.current }));
            if (!createdTodo) {
              throw new Error('Todoの作成結果を取得できませんでした');
            }

            if (session !== editingSessionRef.current) return true;
            createdTodoRef.current = createdTodo;
            savedTodoRef.current = createdTodo;
            setIsCreated(true);

            // 作成中に追加入力があれば、新規作成せず作成済みTodoを更新する
            const latestTodo = latestLocalTodoRef.current ?? todoToCreate;
            const updatedTodo = {
              ...latestTodo,
              id: createdTodo.id,
              createdAt: createdTodo.createdAt,
              modifiedAt: createdTodo.modifiedAt,
            };
            setLocalTodo(updatedTodo);
            latestLocalTodoRef.current = updatedTodo;
            onCreated?.(createdTodo);
            if (JSON.stringify(latestTodo) !== JSON.stringify(todoToCreate)) {
              const saved = await Promise.resolve(onSave?.(updatedTodo, savedTodoRef.current ?? createdTodoRef.current ?? undefined));
              applySavedTodo(saved || updatedTodo, updatedTodo);
            }
          } finally {
            isCreatingRef.current = false;
          }
        }
      } else {
        // 何をしているか: 既存 Todo を更新
        const saved = await Promise.resolve(onSave?.(todo, savedTodoRef.current ?? undefined));
        applySavedTodo(saved || todo, todo);
      }

      if (session !== editingSessionRef.current) return true;
      // A response acknowledges its snapshot, not input typed while it was in flight.
      const current = latestLocalTodoRef.current;
      const saved = savedTodoRef.current;
      const fields = ['title', 'body', 'done', 'canceled', 'deleted', 'startAt', 'doneAt', 'habitId', 'todoTagIds', 'stockItems'] as const;
      const stillDirty = !!current && (!saved || fields.some(field => JSON.stringify(current[field]) !== JSON.stringify(saved[field])));
      setSaveStatus(stillDirty ? 'dirty' : 'success');
      if (!stillDirty) clearSuccessMessage();
      return true;
    } catch (error) {
      if (session !== editingSessionRef.current) return false;
      if (error instanceof TodoSyncConflictError) { conflictRef.current = error; setConflict(error); }
      // 保存失敗
      const errorMessage = error instanceof Error ? error.message : '保存に失敗しました';
      setSaveError(errorMessage);
      setSaveStatus('error');
      return false;
    }
  }, [onSave, onCreate, onCreated, isNewTodo, hasNewTodoInput, clearSuccessMessage, applySavedTodo]);

  const performSave = useCallback((todo: Todo) => {
    const session = editingSessionRef.current;
    const next = saveQueueRef.current.catch(() => false).then(() => session === editingSessionRef.current ? performSaveNow(latestLocalTodoRef.current ?? todo) : true);
    saveQueueRef.current = next;
    return next;
  }, [performSaveNow]);

  const resolveConflict = async (useLocal: boolean) => {
    if (!conflict?.remote || !latestLocalTodoRef.current) return;
    try {
      let nextTodo = { ...latestLocalTodoRef.current };
      if (conflict.resource === 'todos') {
        const before = savedTodoRef.current ?? nextTodo;
        const remoteTodo = toCamelCase(conflict.remote as unknown as TodoEntity);
        const merged = { ...remoteTodo, todoTagIds: nextTodo.todoTagIds, stockItems: nextTodo.stockItems };
        const fields = { title: 'title', body: 'body', done: 'is_done', canceled: 'is_canceled', deleted: 'is_deleted', startAt: 'start_at', doneAt: 'done_at', habitId: 'habit_id' } as const;
        (Object.keys(fields) as Array<keyof typeof fields>).forEach(field => {
          if ((nextTodo[field] ?? null) !== (before[field] ?? null) && (useLocal || !conflict.fields.includes(fields[field]))) {
            Object.assign(merged, { [field]: nextTodo[field] });
          }
        });
        nextTodo = merged;
        savedTodoRef.current = remoteTodo;
      } else {
        const changes: SyncRow = useLocal ? conflict.changes : Object.fromEntries(Object.entries(conflict.changes).filter(([field]) => !conflict.fields.includes(field)));
        if (Object.keys(changes).length) await syncTodoChange(conflict.resource, conflict.key, conflict.remote, changes);
        if (!useLocal && conflict.resource === 'todo_tag_map') {
          const id = String(conflict.key.tag_id);
          nextTodo.todoTagIds = (nextTodo.todoTagIds ?? []).filter(tagId => tagId !== id);
          if (!conflict.remote.is_deleted) nextTodo.todoTagIds.push(id);
        } else if (!useLocal && conflict.resource === 'todo_stock_items') {
          const id = String(conflict.key.stock_id);
          nextTodo.stockItems = (nextTodo.stockItems ?? []).filter(item => item.stock_id !== id);
          if (!conflict.remote.is_deleted) nextTodo.stockItems.push({ stock_id: id, is_checked: !!conflict.remote.is_checked, to_buy: Number(conflict.remote.to_buy ?? 0) });
        }
      }
      latestLocalTodoRef.current = nextTodo;
      setLocalTodo(nextTodo);
      conflictRef.current = null;
      setConflict(null);
      setSaveStatus('dirty');
      // The next save uses the newly selected baseline, and still checks for intervening updates.
      const saved = await Promise.resolve(onSave?.(nextTodo, savedTodoRef.current ?? undefined));
      applySavedTodo(saved || nextTodo, nextTodo);
      setSaveStatus('success');
      setSaveError(undefined);
    } catch (error) {
      if (error instanceof TodoSyncConflictError) { conflictRef.current = error; setConflict(error); }
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました。');
      setSaveStatus('error');
    }
  };

  // デバウンス保存関数
  const debouncedSave = useCallback((todo: Todo) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      performSave(todo);
    }, 200);
  }, [performSave]);
  
  // 即座保存関数
  const immediateSave = useCallback((todo: Todo) => {
    if (isComposingRef.current) {
      // 日本語入力の変換中は新規作成を行わない
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    performSave(todo);
  }, [performSave]);

  const applyHabitDefaultTagsIfNeeded = useCallback((targetTodo: Todo, habitId?: string) => {
    if (!habitId) return targetTodo;

    const defaultTagIds = getHabitTagIds(habitId);
    const currentTagIds = targetTodo.todoTagIds || [];
    const currentMatchesPreviousDefault =
      !!lastAppliedHabitDefaultSourceHabitIdRef.current &&
      currentTagIds.length === lastAppliedHabitDefaultTagIdsRef.current.length &&
      currentTagIds.every(tagId => lastAppliedHabitDefaultTagIdsRef.current.includes(tagId));
    const shouldApply =
      currentTagIds.length === 0 || currentMatchesPreviousDefault;

    if (!shouldApply) return targetTodo;

    lastAppliedHabitDefaultTagIdsRef.current = defaultTagIds;
    lastAppliedHabitDefaultSourceHabitIdRef.current = habitId;
    return {
      ...targetTodo,
      todoTagIds: defaultTagIds,
    };
  }, [getHabitTagIds]);
  
  // Todo項目が変わった時にローカル状態を更新（ID 変更時のみ）
  useEffect(() => {
    if (todo) {
      if (prevTodoIdRef.current !== todo.id) {
        prevTodoIdRef.current = todo.id;
        if (createdTodoRef.current?.id === todo.id) return;
        editingSessionRef.current += 1;

        // タイトルは習慣とは独立して保持（表示時に習慣名を優先表示に変更）
        const initialTodo = applyHabitDefaultTagsIfNeeded(
          { ...todo, stockItems: todo.stockItems || [] },
          todo.habitId
        );
        setLocalTodo(initialTodo);
        setSaveStatus('idle');
        setSaveError(undefined);
        // 新しい下書きを開いた時だけ作成状態をリセットする。
        // 作成後に親からID付きTodoが届くタイミングではrefを残し、古いデバウンス処理の重複作成を防ぐ。
        if (todo.id === '') {
          setIsCreated(false);
          createdTodoRef.current = null;
          creationIDRef.current = null;
        }
        latestLocalTodoRef.current = initialTodo;
        savedTodoRef.current = todo;
        setConflict(null);
        conflictRef.current = null;
        setIsHabitSelected(!!todo.habitId);
      }
    } else {
      editingSessionRef.current += 1;
      prevTodoIdRef.current = null;
      setLocalTodo(null);
      setSaveStatus('idle');
      setSaveError(undefined);
      setIsCreated(false);
      createdTodoRef.current = null;
      latestLocalTodoRef.current = null;
      savedTodoRef.current = null;
      creationIDRef.current = null;
      setConflict(null);
      conflictRef.current = null;
      setIsHabitSelected(false);
    }
  }, [todo, activeHabits, applyHabitDefaultTagsIfNeeded]);
  
  
  // 確実保存関数
  const forceSave = useCallback(async (): Promise<boolean> => {
    const current = latestLocalTodoRef.current;
    const baseline = savedTodoRef.current;
    const fields = ['title', 'body', 'done', 'canceled', 'deleted', 'startAt', 'doneAt', 'habitId', 'todoTagIds', 'stockItems'] as const;
    const dirty = !!current && (!baseline || fields.some(field => JSON.stringify(current[field]) !== JSON.stringify(baseline[field])));
    if (current && dirty) {
      if (isComposingRef.current) return false;
      // 新規作成の場合は日本語入力の変換確定前や未入力なら保存しない
      if (isNewTodo) {
        if (!hasNewTodoInput(current)) return true;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return performSave(current);
    }
    return !conflictRef.current;
  }, [isNewTodo, hasNewTodoInput, performSave]);
  
  // クリーンアップ: タイマーをクリア
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // ドロワーが開いた時の処理
  useEffect(() => {
    if (isOpen) {
      // body要素のスクロールを無効化
      document.body.style.overflow = 'hidden';
    } else {
      // body要素のスクロールを復元
      document.body.style.overflow = '';
    }

    // クリーンアップ
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, todo]);


  // ESCキーでドロワーを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        event.preventDefault();
        const session = editingSessionRef.current;
        forceSave().then(saved => { if (saved && session === editingSessionRef.current) onClose(); });
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose, forceSave]);

  
  // ドロワーが閉じる時に確実保存
  const handleClose = useCallback(async () => {
    const session = editingSessionRef.current;
    if (await forceSave() && session === editingSessionRef.current) onClose();
  }, [forceSave, onClose]);

  // 買い物ドロップダウン: 外側クリックとESCで閉じる（MenuButtonの挙動に寄せる）
  useEffect(() => {
    if (!showShoppingDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (shoppingWrapperRef.current && !shoppingWrapperRef.current.contains(event.target as Node)) {
        setShowShoppingDropdown(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowShoppingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showShoppingDropdown]);
  
  // Todo切り替え時に確実保存


  // 共通のフィールド更新ハンドラー
  const updateField = (field: keyof Todo, value: any) => {
    const current = latestLocalTodoRef.current;
    if (current) {
      const updatedTodo = { ...current, [field]: value };
      setLocalTodo(updatedTodo);
      latestLocalTodoRef.current = updatedTodo;
      
      // 未保存状態をセット（保存処理が開始されるまで）
      if (saveStatus === 'idle' || saveStatus === 'success') {
        setSaveStatus('dirty');
      }
      
      // 仕様書に従った保存戦略
      if (['done', 'startAt', 'todoTagIds', 'stockItems'].includes(field)) {
        // 重要データ: 即座保存
        immediateSave(updatedTodo);
      } else if (['title', 'body'].includes(field)) {
        // テキストデータ: IME入力中でなければ200ms遅延保存
        if (!isComposingRef.current) {
          debouncedSave(updatedTodo);
        }
      }
    } else {
    }
  };


  // Body用のIME入力イベントハンドラー
  const handleBodyCompositionStart = () => {
    isComposingRef.current = true;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  const handleBodyCompositionEnd = (event: React.CompositionEvent<HTMLTextAreaElement>) => {
    isComposingRef.current = false;
    
    const value = event.currentTarget.value;
    const current = latestLocalTodoRef.current;
    if (current) {
      const updated = { ...current, body: value };
      setLocalTodo(updated);
      latestLocalTodoRef.current = updated;
      
      if (saveStatus === 'idle' || saveStatus === 'success') {
        setSaveStatus('dirty');
      }
      
      immediateSave(updated);
    }
  };


  const handleDoneChange = () => {
    if (localTodo) {
      updateField('done', !localTodo.done);
    }
  };

  const handleDateChange = (isoString: string | null) => {
    if (localTodo && isoString) {
      const current = getTokyoDateParts(localTodo.startAt);
      const selected = getTokyoDateParts(isoString);
      const newDate = createTokyoDateTime({ ...selected, hour: current.hour, minute: current.minute, second: current.second });

      updateField('startAt', newDate.toISOString());
      
      // 日付変更を親コンポーネントに通知
      onDateChange?.(formatTokyoDate(newDate));
    }
  };

  const handleTimeChange = (isoString: string | null) => {
    if (localTodo && isoString) {
      const current = getTokyoDateParts(localTodo.startAt);
      const selected = getTokyoDateParts(isoString);
      const updated = createTokyoDateTime({ ...current, hour: selected.hour, minute: selected.minute, second: 0 });

      updateField('startAt', updated.toISOString());
    }
  };



  // 現在の習慣タイトルを取得
  const getCurrentHabitTitle = () => {
    if (localTodo?.habitId) {
      const habit = activeHabits.find(h => h.id === localTodo.habitId);
      return habit?.title || '';
    }
    return '';
  };

  // タグ選択の変更ハンドラー
  const handleTagSelectionChange = (selectedTagIds: string[]) => {
    if (localTodo) {
      lastAppliedHabitDefaultTagIdsRef.current = selectedTagIds;
      lastAppliedHabitDefaultSourceHabitIdRef.current = undefined;
      updateField('todoTagIds', selectedTagIds);
    }
  };

  // 新規タグ作成ハンドラー
  const handleAddNewTag = async (title: string) => {
    const newTag = await createNewTag(title);
    if (localTodo) {
      const currentTagIds = localTodo.todoTagIds || [];
      if (!currentTagIds.includes(newTag.id)) {
        handleTagSelectionChange([...currentTagIds, newTag.id]);
      }
    }
  };

  // TextAreaの行数自動調整関数は不要

  
  // todoがない場合でもコンポーネントは表示し、isOpenで制御する

  return (
    <div 
      className={`drawer-todo-detail ${isOpen ? 'drawer-todo-detail--open' : ''}`}
    >
      {/* ドロワー本体 */}
      <div 
        ref={drawerRef}
        className="drawer-todo-detail__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {conflict && <section className="drawer-todo-detail__conflict" role="alert" aria-label="競合した内容">
          <p>別の編集と競合しています。入力を保持しています。本文は下の入力欄で編集できます。</p>
          {conflict.fields.map(field => <div key={field}>
            <p>{({ title: 'タイトル', body: '本文', is_done: '完了', is_canceled: 'キャンセル', is_deleted: '削除', start_at: '開始日時', done_at: '完了日時', habit_id: '習慣', is_checked: '購入済み', to_buy: '購入数' } as Record<string, string>)[field] ?? '変更内容'}</p>
            <p>この端末：{String(conflict.changes[field] ?? '未設定')}</p>
            <p style={{ whiteSpace: 'pre-wrap' }}>サーバー：{String(conflict.remote?.[field] ?? '未設定')}</p>
          </div>)}
          <button disabled={!conflict.remote} onClick={() => resolveConflict(true)}>この画面の編集を採用</button>
          <button disabled={!conflict.remote} onClick={() => resolveConflict(false)}>競合項目はサーバーの内容を採用</button>
        </section>}
        {/* Functions - アクション ボタン群 */}
        <div className="drawer-todo-detail__functions">
          <div className="drawer-todo-detail__manipulate-drawer">
            <div className="drawer-todo-detail__close-section">
              <IconButton
                icon="close"
                onClick={handleClose}
                tooltip="閉じる"
                tooltipPosition="bottom-right"
                aria-label="閉じる"
              />
            </div>
            <div className="drawer-todo-detail__middle-section">
              <IconButton
                icon="chevronUp"
                onClick={async () => { const session = editingSessionRef.current; if (await forceSave() && session === editingSessionRef.current) onPreviousTodo?.(); }}
                tooltip="前の予定"
                tooltipPosition="bottom-right"
                aria-label="前の予定"
                disabled={!hasPreviousTodo}
              />
              <IconButton
                icon="chevronDown"
                onClick={async () => { const session = editingSessionRef.current; if (await forceSave() && session === editingSessionRef.current) onNextTodo?.(); }}
                tooltip="次の予定"
                tooltipPosition="bottom-right"
                aria-label="次の予定"
                disabled={!hasNextTodo}
              />
              {/* Save status feedback */}
              <SaveStatusFeedback 
                status={saveStatus}
                errorMessage={saveError}
              />
            </div>
          </div>
          <div className="drawer-todo-detail__manipulate-todo">
            <MenuButton
              icon="menu"
              tooltip="Todo 操作メニュー"
              tooltipPosition="bottom-left"
              aria-label="Todo 操作メニュー"
              stopPropagation={false}
              items={[
                {
                  type: 'Destructive',
                  label: '削除する',
                  leftIcon: 'delete',
                  showLeftIcon: true,
                  onClick: () => {
                    if (localTodo && localTodo.id !== '') {
                      onDelete?.(localTodo.id);
                    }
                  }
                }
              ]}
            />
          </div>
        </div>

        {/* Header - タイトルと日時入力 */}
        <div className="drawer-todo-detail__header">
          <div className="drawer-todo-detail__inputs">
            {/* タイトル入力とチェックボックス */}
            <div className="drawer-todo-detail__title-input">
              <div className="drawer-todo-detail__title-with-checkbox">
                <Checkbox
                  checked={localTodo?.done || false}
                  onChange={handleDoneChange}
                  aria-label="Todo完了状態"
                />
                <InputTodoTitle
                  title={isHabitSelected ? (getCurrentHabitTitle() || localTodo?.title || '') : (localTodo?.title || '')}
                  habitId={localTodo?.habitId}
                  activeHabits={activeHabits}
                  onTitleTyping={(value) => {
                    if (!localTodo) return;
                    const updated = { ...localTodo, title: value };
                    setLocalTodo(updated);
                    latestLocalTodoRef.current = updated;
                    if (saveStatus === 'idle' || saveStatus === 'success') setSaveStatus('dirty');
                    if (!isComposingRef.current) {
                      debouncedSave(updated);
                    }
                  }}
                  onTitleCommit={(value) => {
                    if (!localTodo) return;
                    const updated = { ...localTodo, title: value };
                    setLocalTodo(updated);
                    latestLocalTodoRef.current = updated;
                    if (saveStatus === 'idle' || saveStatus === 'success') setSaveStatus('dirty');
                    immediateSave(updated);
                  }}
                  onCompositionStart={() => { isComposingRef.current = true; }}
                  onCompositionEnd={() => { isComposingRef.current = false; }}
                  onHabitSelect={(habit) => {
                    if (!localTodo) return;
                    const updated = applyHabitDefaultTagsIfNeeded({ ...localTodo, habitId: habit.id }, habit.id);
                    setLocalTodo(updated);
                    latestLocalTodoRef.current = updated;
                    if (saveStatus === 'idle' || saveStatus === 'success') setSaveStatus('dirty');
                    immediateSave(updated);
                    setIsHabitSelected(true);
                  }}
                  onHabitClear={(_currentTitle) => {
                    if (!localTodo) return;
                    // タイトルは変更せず、habitId のみ解除
                    const updated = { ...localTodo, habitId: undefined };
                    setLocalTodo(updated);
                    latestLocalTodoRef.current = updated;
                    if (saveStatus === 'idle' || saveStatus === 'success') setSaveStatus('dirty');
                    immediateSave(updated);
                    setIsHabitSelected(false);
                  }}
                  onAddNewHabit={async (title) => {
                    const existing = activeHabits.find(h => h.title === title);
                    const habit = existing ?? await createHabit({ title });
                    if (!localTodo) return;
                    const updated = applyHabitDefaultTagsIfNeeded({ ...localTodo, habitId: habit.id }, habit.id);
                    setLocalTodo(updated);
                    latestLocalTodoRef.current = updated;
                    if (saveStatus === 'idle' || saveStatus === 'success') setSaveStatus('dirty');
                    immediateSave(updated);
                    setIsHabitSelected(true);
                  }}
                />
              </div>
            </div>
            
            {/* 日時入力とタグボタン */}
            <div className="drawer-todo-detail__datetime-row">
              <div className="drawer-todo-detail__datetime-inputs">
                <div className="drawer-todo-detail__date-input">
                  <InputDate
                    value={localTodo?.startAt}
                    onChange={handleDateChange}
                    closeOnSelect={viewMode !== 'date'}
                  />
                </div>
                <div className="drawer-todo-detail__time-input">
                  <InputTime
                    value={localTodo?.startAt}
                    onChange={handleTimeChange}
                  />
                </div>
              </div>
              <div className="drawer-todo-detail__tag-functions">
                {/* settings MenuButton for habit selection removed */}
                <IconButton
                  icon="tag"
                  onClick={() => setShowTagInput(!showTagInput)}
                  tooltip="タグを設定"
                  aria-label="タグを設定"
                  tooltipPosition="top-left"
                  disabled={isShoppingHabitId(localTodo?.habitId)}
                />
              </div>
            </div>
          </div>
          {/* Tags input */}
          {showTagInput && !isShoppingHabitId(localTodo?.habitId) && (
            <div className="drawer-todo-detail__tags">
              <InputTodoTags
                selectedTagIds={localTodo?.todoTagIds || []}
                availableTags={activeTags}
                isOpen={showTagInput}
                onTagSelectionChange={handleTagSelectionChange}
                onAddNewTag={handleAddNewTag}
                onClose={() => setShowTagInput(false)}
              />
            </div>
          )}
        </div>



        {/* Content - スクロール可能なコンテンツエリア */}
        <div className="drawer-todo-detail__content">
          {(() => {
            return isShoppingHabitId(localTodo?.habitId);
          })() ? (
            <div className="drawer-todo-detail__shopping-list">
              <div className="drawer-todo-detail__shopping-search-wrapper" ref={shoppingWrapperRef}>
                <input
                  type="text"
                  className="drawer-todo-detail__shopping-search text-single-large"
                  placeholder="買い物リストを検索・追加..."
                  value={shoppingSearchTerm}
                  onChange={(e) => setShoppingSearchTerm(e.target.value)}
                  onFocus={() => {
                    setShowShoppingDropdown(true);
                  }}
                />
                {(() => {
                  return showShoppingDropdown;
                })() && (
                  <div className="drawer-todo-detail__shopping-dropdown">
                    {(() => {
                      const currentStockItems = localTodo?.stockItems || [];
                      const term = shoppingSearchTerm.toLowerCase();
                      const filtered = activeStocks.filter(stock => {
                        if (currentStockItems.some(item => item.stock_id === stock.id)) return false;
                        if (!term) return true;
                        return stock.title.toLowerCase().includes(term);
                      });

                      const sortByCountTitle = (a: any, b: any) => {
                        const ca = (a.count ?? 0);
                        const cb = (b.count ?? 0);
                        if (ca !== cb) return ca - cb;
                        return a.title.localeCompare(b.title, 'ja');
                      };

                      const groups: { id: string; title: string; stocks: typeof filtered }[] = [];
                      // カテゴリ定義順
                      activeStockCategories.forEach(cat => {
                        const stocksInCat = filtered.filter(s => s.stock_category_id === cat.id).sort(sortByCountTitle);
                        groups.push({ id: cat.id, title: cat.title, stocks: stocksInCat });
                      });
                      // 未分類（最後）
                      const uncategorized = filtered.filter(s => !s.stock_category_id).sort(sortByCountTitle);
                      groups.push({ id: 'uncategorized', title: '未分類', stocks: uncategorized });

                      return groups.map(group => (
                        group.stocks.length > 0 ? (
                          <div key={`group-${group.id}`} className="drawer-todo-detail__shopping-group">
                            <div className="drawer-todo-detail__shopping-group-title text-body-small">
                              <Icon name="folder" size={16} />
                              <span>{group.title}</span>
                            </div>
                            {group.stocks.map(stock => (
                              <div
                                key={stock.id}
                                className="drawer-todo-detail__shopping-item"
                                onMouseDown={(e) => {
                                  e.preventDefault(); // blurを防ぐ
                                }}
                                onClick={() => {
                                  if (localTodo) {
                                    const current = localTodo.stockItems || [];
                                    if (!current.some(item => item.stock_id === stock.id)) {
                                      const newStockItem: StockItem = { stock_id: stock.id, is_checked: false, to_buy: 0 };
                                      const updatedStockItems = [...current, newStockItem];
                                      updateField('stockItems', updatedStockItems);
                                    }
                                    setShoppingSearchTerm('');
                                    setShowShoppingDropdown(false);
                                  }
                                }}
                              >
                                <span className="drawer-todo-detail__shopping-item-title text-body-medium">
                                  {stock.title}
                                </span>
                                {(() => {
                                  const count = stock.count ?? 0;
                                  const low = count <= 1;
                                  return (
                                    <div className="drawer-todo-detail__shopping-item-body-row">
                                      <span className="drawer-todo-detail__shopping-item-body text-body-small">あと</span>
                                      <span className={`drawer-todo-detail__shopping-item-body text-body-small-bold ${low ? 'text-error' : ''}`}>{count}</span>
                                      <span className="drawer-todo-detail__shopping-item-body text-body-small">個</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            ))}
                          </div>
                        ) : null
                      ));
                    })()}

                    {shoppingSearchTerm && !activeStocks.some(stock => 
                      stock.title.toLowerCase() === shoppingSearchTerm.toLowerCase()
                    ) && (
                      <div
                        className="drawer-todo-detail__shopping-add-new"
                        onMouseDown={(e) => {
                          e.preventDefault(); // blurを防ぐ
                        }}
                        onClick={async () => {
                          if (localTodo) {
                            const newStock = await createStock({
                              title: shoppingSearchTerm.trim(),
                              body: ''
                            });
                            const currentStockItems = localTodo.stockItems || [];
                            if (!currentStockItems.some(item => item.stock_id === newStock.id)) {
                              const newStockItem: StockItem = { stock_id: newStock.id, is_checked: false, to_buy: 0 };
                              const updatedStockItems = [...currentStockItems, newStockItem];
                              updateField('stockItems', updatedStockItems);
                            }
                            setShoppingSearchTerm('');
                            setShowShoppingDropdown(false);
                          }
                        }}
                      >
                        <span className="text-body-medium">+ {shoppingSearchTerm} をストックに追加</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* 選択されたストック一覧 */}
              {localTodo?.stockItems && localTodo.stockItems.length > 0 && (
                <div className="drawer-todo-detail__selected-stocks">
                  {(() => {
                    const seen = new Set<string>();
                    const uniqueItems = (localTodo.stockItems || []).filter(i => {
                      if (seen.has(i.stock_id)) return false;
                      seen.add(i.stock_id);
                      return true;
                    });

                    const categoryOrder = new Map<string, number>();
                    activeStockCategories.forEach((c, idx) => categoryOrder.set(c.id, idx));

                    const sortedItems = uniqueItems.slice().sort((a, b) => {
                      const sa = activeStocks.find(s => s.id === a.stock_id);
                      const sb = activeStocks.find(s => s.id === b.stock_id);
                      if (!sa && !sb) return 0;
                      if (!sa) return 1;
                      if (!sb) return -1;

                      const ca = sa.stock_category_id ? (categoryOrder.get(sa.stock_category_id) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
                      const cb = sb.stock_category_id ? (categoryOrder.get(sb.stock_category_id) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
                      if (ca !== cb) return ca - cb;

                      const ao = sa.sort_order ?? 0;
                      const bo = sb.sort_order ?? 0;
                      if (ao !== bo) return ao - bo;

                      return sa.title.localeCompare(sb.title, 'ja');
                    });

                    return sortedItems.map(stockItem => {
                    const stock = activeStocks.find(s => s.id === stockItem.stock_id);
                    if (!stock) return null;
                    
                    return (
                      <div key={stockItem.stock_id} className="drawer-todo-detail__selected-stock-item">
                        <Checkbox
                          checked={stockItem.is_checked}
                          onChange={(checked) => {
                            const updatedStockItems = localTodo.stockItems?.map(item => 
                              item.stock_id === stockItem.stock_id 
                                ? { ...item, is_checked: checked } 
                                : item
                            ) || [];
                            updateField('stockItems', updatedStockItems);
                          }}
                          aria-label={`${stock.title}をチェック`}
                        />
                        <div className="drawer-todo-detail__selected-stock-content">
                          <span className="drawer-todo-detail__selected-stock-title text-body-medium">
                            {stock.title}
                          </span>
                          {(() => {
                            const count = stock.count ?? 0;
                            const low = count <= 1;
                            return (
                              <div className="drawer-todo-detail__selected-stock-body-row">
                                <span className="drawer-todo-detail__selected-stock-body text-body-small">あと</span>
                                <span className={`drawer-todo-detail__selected-stock-body text-body-small ${low ? 'text-error' : ''}`}>{count}</span>
                                <span className="drawer-todo-detail__selected-stock-body text-body-small">個</span>
                              </div>
                            );
                          })()}
                        </div>


                        <div className="drawer-todo-detail__selected-stock-body-row__number-to-buy" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                          <IconButton
                            icon="minus"
                            onClick={() => {
                              if (!localTodo) return;
                              const current = stockItem.to_buy ?? 0;
                              const next = Math.max(0, current - 1);
                              const updated = (localTodo.stockItems || []).map(it =>
                                it.stock_id === stockItem.stock_id ? { ...it, to_buy: next } : it
                              );
                              updateField('stockItems', updated);
                            }}
                            tooltip="買う数を減らす"
                            tooltipPosition="bottom-left"
                            aria-label="買う数を減らす"
                            disabled={(stockItem.to_buy ?? 0) <= 0}
                          />
                          <InputNumber
                            hasLeftIcon={true}
                            leftIcon="shop"
                            placeholder="買う数"
                            value={stockItem.to_buy ?? 0}
                            min={0}
                            step={1}
                            hideSpinner={true}
                            width={80}
                            onChange={e => {
                              const v = e.target.value === '' ? 0 : Number(e.target.value);
                              if (!localTodo) return;
                              const updated = (localTodo.stockItems || []).map(it =>
                                it.stock_id === stockItem.stock_id ? { ...it, to_buy: Math.max(0, v) } : it
                              );
                              updateField('stockItems', updated);
                            }}
                            aria-label={`${stock.title}の買う数`}
                          />
                          <IconButton
                            icon="add"
                            onClick={() => {
                              if (!localTodo) return;
                              const current = stockItem.to_buy ?? 0;
                              const next = current + 1;
                              const updated = (localTodo.stockItems || []).map(it =>
                                it.stock_id === stockItem.stock_id ? { ...it, to_buy: next } : it
                              );
                              updateField('stockItems', updated);
                            }}
                            tooltip="買う数を増やす"
                            tooltipPosition="bottom-left"
                            aria-label="買う数を増やす"
                          />
                        </div>


                        <div className="drawer-todo-detail__selected-stock-actions">
                          <MenuButton
                            icon="menu"
                            tooltip="買い物リスト操作"
                            tooltipPosition="bottom-left"
                            aria-label="買い物リスト操作メニュー"
                            stopPropagation={false}
                            menuWidth={240}
                            items={[
                              {
                                type: 'Default',
                                label: '買い物リストからはずす',
                                leftIcon: 'close',
                                showLeftIcon: true,
                                onClick: () => {
                                  if (localTodo) {
                                    const currentStockItems = localTodo.stockItems || [];
                                    const updatedStockItems = currentStockItems.filter(item => item.stock_id !== stockItem.stock_id);
                                    updateField('stockItems', updatedStockItems);
                                  }
                                }
                              }
                            ]}
                          />
                        </div>
                      </div>
                    );
                  });
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="drawer-todo-detail__markdown-content">
              <textarea
                className="drawer-todo-detail__body-textarea text-body-medium"
                value={localTodo?.body || ''}
                onChange={(e) => updateField('body', e.target.value)}
                onCompositionStart={handleBodyCompositionStart}
                onCompositionEnd={handleBodyCompositionEnd}
                placeholder="詳細内容を入力してください..."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrawerTodoDetail;
